import dayjs, { Dayjs } from "dayjs";

export enum WS_CON_STATE {
  CONNECTING = "CONNECTING",
  CONNECTED = "CONNECTED",
  UNSTABLE = "UNSTABLE",
  RECONNECTING = "RECONNECTING",
  CLOSED = "CLOSED",
}

interface InvMessage {
  e: string;
  d?: any;
}

class InvWebsocket {
  private ws: WebSocket | null;
  private url: string;
  private conState: WS_CON_STATE;
  private lastPing: Dayjs;
  private messageListeners: {
    [label: string]: (msg: InvMessage) => void;
  } = {};
  private conStateListeners: {
    [label: string]: (state: WS_CON_STATE) => void;
  };
  private consecutiveMissedPings: number;
  private pingCheck: number;

  constructor(url: string) {
    this.url = url;
    this.consecutiveMissedPings = 0;
    this.conStateListeners = {};
    this.messageListeners = {};
    this.conState = WS_CON_STATE.CONNECTING;
    this.createCon();
    this.lastPing = dayjs();
  }

  /**
   * If no ping in the last 7 seconds from server, connection
   * marked as UNSTABLE, otherwise mark as CONNECTED. Recreate
   * connection after missing 2 consecutive pings.
   */
  // private startPingCheck = () => {
  //   this.pingCheck = setInterval(() => {

  //     this.ws.

  //     if (
  //       this.lastPing &&
  //       this.lastPing.isBefore(dayjs().subtract(7, "seconds"))
  //     ) {
  //       this.conStateChange(WS_CON_STATE.UNSTABLE);

  //       // if we miss two pings in a row, we re-create connection.
  //       if (++this.consecutiveMissedPings === 2) {
  //         this.stopPingCheck();
  //         this.reconnect();
  //       }
  //     } else if (this.conState !== WS_CON_STATE.CONNECTED) {
  //       this.conStateChange(WS_CON_STATE.CONNECTED);
  //     }
  //   }, 7000);
  // };

  private stopPingCheck = () => {
    clearInterval(this.pingCheck);
    this.consecutiveMissedPings = 0;
  };

  /**
   * Alert all connection state listeners of state change.
   */
  private conStateChange = (state: WS_CON_STATE) => {
    this.conState = state;
    for (const listener of Object.values(this.conStateListeners)) {
      listener(state);
    }
  };

  /**
   * Close the current connection and make a new one.
   */
  private reconnect = () => {
    this.conStateChange(WS_CON_STATE.RECONNECTING);
    this.ws?.close();
    this.createCon();
  };

  /**
   * Create a ws connection and resolves if connection is successful. Reject otherwise.
   */
  private establishCon = () => {
    return new Promise<WebSocket>((resolve, reject) => {
      const ws = new WebSocket(this.url);
      ws.addEventListener("open", () => {
        resolve(ws);
      });
      ws.addEventListener("error", (err) => {
        console.log(err);
        reject();
      });
    });
  };

  /**
   * Continuously attempt to create a successful connection.
   * Once created, attach message listener and start ping check.
   */
  private createCon = async () => {
    let failed: boolean;

    do {
      if (this.conState === WS_CON_STATE.CLOSED) {
        return;
      }
      failed = true;
      try {
        this.ws = await this.establishCon();
        failed = false;
      } catch (err) {
        await sleep(5000);
      }
    } while (failed);

    this.conStateChange(WS_CON_STATE.CONNECTED);
    // this.startPingCheck();

    // create main message listener.
    this.ws?.addEventListener("message", (e) => {
      const msg = this.parseEvent(e);
      if (!msg) {
        return;
      }

      if (msg.e === "ping") {
        this.lastPing = dayjs();
        this.send({ e: "pong" });
      }

      for (const listener of Object.values(this.messageListeners)) {
        listener(msg);
      }
    });
  };

  /**
   * Send message out to the server.
   */
  public send = (msg: InvMessage) => {
    if (
      !this.ws ||
      this.ws.readyState === this.ws.CLOSED ||
      this.ws.readyState === this.ws.CLOSING
    ) {
      console.log("not sending message");
      return;
    }
    this.ws.send(JSON.stringify(msg));
  };

  /**
   * Parses websocket event into RF ws message standard.
   */
  private parseEvent = (e: MessageEvent<any>): InvMessage | null => {
    let msg: any;
    try {
      msg = JSON.parse(e.data);
      if (
        typeof msg.e !== "string" ||
        (msg.d !== undefined && typeof msg.d !== "object")
      ) {
        return null;
      }
    } catch (err) {
      return null;
    }

    return {
      e: msg.e,
      d: msg.d,
    };
  };

  public addConStateListener = (
    label: string,
    fn: (state: WS_CON_STATE) => void
  ) => {
    if (this.conStateListeners[label]) {
      throw new Error(
        "Connection state listener with this label already exists."
      );
    }
    this.conStateListeners[label] = fn;
  };

  public removeConStateListener = (label: string) => {
    const listener = this.messageListeners[label];
    if (!listener) {
      return;
    }
    delete this.messageListeners[label];
  };

  public addMessageListener = (label: string, fn: (msg: InvMessage) => void) => {
    if (this.messageListeners[label]) {
      throw new Error("Message listener with this label already exists.");
    }
    this.messageListeners[label] = fn;
  };

  public removeMessageListener = (label: string) => {
    const listener = this.messageListeners[label];
    if (!listener) {
      return;
    }
    delete this.messageListeners[label];
  };

  public destroy = () => {
    this.ws?.close();
    this.ws = null;
    this.conStateChange(WS_CON_STATE.CLOSED);
    clearInterval(this.pingCheck);
  };
}

const sleep = (ms: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

export default InvWebsocket;
